import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  TAssignmentCandidate,
  TAssignmentCandidatesPage,
  TAssignmentCandidatesQuery,
} from "@/types/assignment-candidates";

/** The most work items the bulk candidates endpoint takes in one selection. */
export const MAX_BULK_CANDIDATE_WORK_ITEMS = 200;
/** The endpoints' largest page: the pickers that list the whole pool walk it in these. */
const CANDIDATES_PAGE_SIZE = 100;

export class IssueMetadataService extends ApiService {
  async listAssignmentCandidates(
    slug: string,
    projectId: string,
    workItemId: string,
    { q, offset, limit }: TAssignmentCandidatesQuery = {},
  ): Promise<TAssignmentCandidatesPage> {
    const search = buildCandidatesSearch({ q, offset, limit });
    return this.get(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${workItemId}/assignment-candidates/${search}`,
    );
  }

  // Draft variant for the create dialog: no work item exists yet, so the
  // ranking window travels as explicit params.
  async listDraftAssignmentCandidates(
    slug: string,
    projectId: string,
    {
      q,
      offset,
      limit,
      startDate,
      endDate,
      estimateHours,
      capLoadToWorkingHours,
    }: TAssignmentCandidatesQuery & {
      startDate?: string;
      endDate?: string;
      estimateHours?: string;
      // Events: cap each candidate's load to their working day so an all-day
      // slot doesn't read as 24h of work (see ProjectAssignmentCandidatesEndpoint).
      capLoadToWorkingHours?: boolean;
    } = {},
    config?: ApiRequestConfig,
  ): Promise<TAssignmentCandidatesPage> {
    const search = buildCandidatesSearch(
      { q, offset, limit },
      {
        start_date: startDate,
        end_date: endDate,
        estimate_hours: estimateHours,
        cap_load_to_working_hours: capLoadToWorkingHours ? "true" : undefined,
      },
    );
    return this.get(
      `/api/workspaces/${slug}/projects/${projectId}/assignment-candidates/${search}`,
      config,
    );
  }

  async listWorkspaceDraftAssignmentCandidates(
    slug: string,
    {
      q,
      offset,
      limit,
      startDate,
      endDate,
      estimateHours,
      capLoadToWorkingHours,
    }: TAssignmentCandidatesQuery & {
      startDate?: string;
      endDate?: string;
      estimateHours?: string;
      capLoadToWorkingHours?: boolean;
    } = {},
  ): Promise<TAssignmentCandidatesPage> {
    const search = buildCandidatesSearch(
      { q, offset, limit },
      {
        start_date: startDate,
        end_date: endDate,
        estimate_hours: estimateHours,
        cap_load_to_working_hours: capLoadToWorkingHours ? "true" : undefined,
      },
    );
    return this.get(`/api/workspaces/${slug}/assignment-candidates/${search}`);
  }

  /**
   * Every assignee candidate ranked against a selection of work items, for the
   * bulk Assignees picker (NG-3057): availability over the union of their
   * windows and skill match against the union of their required skills. The
   * picker lists the whole pool locally, so this walks every page; `config`
   * goes on each page's request, so a query's abort signal stops the walk.
   */
  async listAllBulkAssignmentCandidates(
    slug: string,
    workItemIds: readonly string[],
    config?: ApiRequestConfig,
  ): Promise<TAssignmentCandidate[]> {
    return collectAllPages((offset) =>
      this.get(
        `/api/workspaces/${slug}/assignment-candidates/bulk/${buildCandidatesSearch(
          { offset, limit: CANDIDATES_PAGE_SIZE },
          { work_item_ids: workItemIds.join(",") },
        )}`,
        config,
      ),
    );
  }

  /**
   * Every {@link listDraftAssignmentCandidates} page, for the Smart Assignment
   * panel's pick-someone-else list (NG-3289): the task Assignee field's pool,
   * ranked over a job role's window and total estimate. Without a window the
   * list is alphabetical with no hours, as for a task with no dates. `config`
   * goes on each page's request, so a query's abort signal stops the walk.
   */
  async listAllDraftAssignmentCandidates(
    slug: string,
    projectId: string,
    window: { startDate?: string; endDate?: string; estimateHours?: string },
    config?: ApiRequestConfig,
  ): Promise<TAssignmentCandidate[]> {
    return collectAllPages((offset) =>
      this.listDraftAssignmentCandidates(
        slug,
        projectId,
        { ...window, offset, limit: CANDIDATES_PAGE_SIZE },
        config,
      ),
    );
  }
}

/** Walk a paged candidates endpoint from offset 0 to its last page. */
async function collectAllPages(
  fetchPage: (offset: number) => Promise<TAssignmentCandidatesPage>,
): Promise<TAssignmentCandidate[]> {
  const all: TAssignmentCandidate[] = [];
  let offset: number | null = 0;
  while (offset !== null) {
    const page = await fetchPage(offset);
    all.push(...page.results);
    // Stop at the last page, or if the server fails to advance.
    offset = page.next_offset !== null && page.next_offset > offset ? page.next_offset : null;
  }
  return all;
}

function buildCandidatesSearch(
  { q, offset, limit }: TAssignmentCandidatesQuery,
  extra: Record<string, string | undefined> = {},
): string {
  const params = new URLSearchParams();
  const query = q?.trim();
  if (query) params.set("q", query);
  if (offset) params.set("offset", String(offset));
  if (limit) params.set("limit", String(limit));
  for (const [key, value] of Object.entries(extra)) {
    if (value?.trim()) params.set(key, value.trim());
  }
  return params.size > 0 ? `?${params.toString()}` : "";
}
